import type { BulkResult, BulkTask } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { sumDecimals } from '../../../lib/decimal';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { Money } from '../../../ui/Layout';
import { DocStatusBadge, TaxStatusBadge, taxAxisOf } from '../status';
import styles from './Batch.module.css';
import { BatchTable, type BatchColumn } from './BatchTable';
import { reasonOf, reviewOrder } from './rules';
import { reasonText } from './text';

/**
 * Step 3, the review (BLOCK-1B-BULK-DESIGN §4, BD-6): the server's dry run, one row per document —
 * «سيُنفَّذ» or «سيُتخطّى: reason» — in ONE table, never grouped by buyer, no subtotal and no footer
 * row (AC-B9). The summaries are sentences in paragraphs, never a "label n · label n" tally (AC-B4).
 */
export function BatchReview({
  task,
  results,
  checking,
  onDropSkipped,
}: {
  task: BulkTask;
  /** The dry run's results, in batch order. */
  results: readonly BulkResult[];
  checking: boolean;
  onDropSkipped: (ids: string[]) => void;
}) {
  const { t, formatAmount, formatNumber } = useI18n();
  const rows = reviewOrder(results);
  const willRun = results.filter((r) => r.ok);
  const skipped = results.filter((r) => !r.ok);
  const total = task === 'send' ? sumDecimals(willRun.map((r) => r.totalAmount ?? '0')) : null;

  const columns: BatchColumn<BulkResult>[] = [
    {
      key: 'number',
      header: t.sales.columns.number,
      isPrimary: true,
      cell: (r) => (
        <span className={styles.mono} dir="ltr">
          {r.number ?? '—'}
        </span>
      ),
    },
    {
      key: 'buyer',
      header: t.sales.columns.customer,
      cell: (r) =>
        r.status ? (
          <UserText>{r.cash ? t.sales.cash : (r.receiverName ?? t.sales.noCustomer)}</UserText>
        ) : (
          '—'
        ),
    },
    {
      key: 'state',
      header: `${t.sales.columns.status} · ${t.sales.columns.taxStatus}`,
      cell: (r) => {
        if (!r.status) return '—';
        const axis = taxAxisOf({
          status: r.status,
          readiness: r.readiness ?? null,
          etaStatus: r.etaStatus ?? null,
        });
        return (
          <span className={styles.badges}>
            <DocStatusBadge value={r.status} />
            {axis ? <TaxStatusBadge value={axis} /> : null}
          </span>
        );
      },
    },
    {
      key: 'amount',
      header: t.sales.columns.amount,
      align: 'end',
      cell: (r) => <Money value={r.totalAmount} currency="EGP" />,
    },
    {
      key: 'outcome',
      header: t.batch.review.outcome,
      cell: (r) =>
        r.ok ? (
          <span className={styles.willRun}>{t.batch.review.willRun}</span>
        ) : (
          <span className={styles.willSkip}>
            {t.batch.review.willSkip(reasonText(t, reasonOf(task, r)))}
          </span>
        ),
    },
  ];

  if (checking)
    return (
      <div className={styles.block}>
        <h3 className={styles.blockTitle}>{t.batch.review.title}</h3>
        <p role="status" data-testid="batch-checking">
          {t.batch.review.checking}
        </p>
      </div>
    );
  return (
    <div className={styles.block}>
      <h3 className={styles.blockTitle}>{t.batch.review.title}</h3>
      <BatchTable<BulkResult>
        caption={t.batch.review.title}
        testId="batch-review"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        rowProps={(r) => ({
          'data-testid': `batch-row-${r.number ?? r.id}`,
          'data-outcome': r.ok ? 'run' : 'skip',
          'data-code': r.code,
        })}
      />
      <p
        className={styles.summary}
        data-testid="batch-review-summary"
        data-run={willRun.length}
        data-skip={skipped.length}
      >
        {t.batch.review.summary(
          formatNumber(String(willRun.length)),
          formatNumber(String(skipped.length)),
        )}
      </p>
      {total !== null && willRun.length ? (
        <p className={styles.summary} data-testid="batch-review-total" data-value={total}>
          {t.batch.review.total(formatAmount(total))}
        </p>
      ) : null}
      {willRun.length === 0 ? (
        <p className={styles.summary} data-testid="batch-review-nothing">
          {t.batch.review.nothing}
        </p>
      ) : null}
      {skipped.length ? (
        <div>
          <Button
            variant="ghost"
            className={styles.textButton}
            onPress={() => onDropSkipped(skipped.map((r) => r.id))}
            testId="batch-drop-skipped"
          >
            {t.batch.review.dropSkipped}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
